import React, { useState, useEffect } from 'react';
import { WindowHeader, WindowContent } from '@/components/ui/window-layout';
import { useNotesManager } from '@/contexts/NotesManager';
import { MacOSNotesLayout } from '@/components/notes/MacOSNotesLayout';
import { NotesNavigation } from '@/components/notes/NotesNavigation';
import { NotesListPanel } from '@/components/notes/NotesListPanel';
import { NoteContentPanel } from '@/components/notes/NoteContentPanel';
import { cn } from '@/lib/utils';

type ViewType = 'all' | 'favorites' | 'recent' | 'folder';

export default function NotesWindow() {
  const { 
    notes, 
    folders, 
    addNote, 
    searchNotes, 
    getNotesInFolder, 
    getFavoriteNotes,
    getRecentNotes
  } = useNotesManager();
  
  const [currentView, setCurrentView] = useState<ViewType>('all');
  const [selectedFolderId, setSelectedFolderId] = useState<string | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedNoteId, setSelectedNoteId] = useState<string | undefined>(undefined);
  const [isEditing, setIsEditing] = useState(false);

  // Handle toggle editing - also clears editing when no note is selected
  const handleToggleEditing = () => {
    setIsEditing(!isEditing);
  };


  // Get current notes based on view
  const getCurrentNotes = () => {
    if (searchQuery) {
      return searchNotes(searchQuery);
    }

    switch (currentView) {
      case 'folder':
        return getNotesInFolder(selectedFolderId);
      case 'favorites':
        return getFavoriteNotes();
      case 'recent':
        return getRecentNotes();
      case 'all':
        return notes;
      default:
        return notes;
    }
  };

  const handleNewNote = () => {
    const newNote = {
      title: 'Untitled Note',
      content: '',
      folderId: currentView === 'folder' ? selectedFolderId : undefined,
      tags: [],
      attachments: [],
      isFavorite: false
    };
    
    const createdNote = addNote(newNote);
    setSelectedNoteId(createdNote.id);
    setIsEditing(true);
  };

  const handleViewChange = (view: string, folderId?: string) => {
    setCurrentView(view as ViewType);
    setSelectedFolderId(folderId);
    setSelectedNoteId(undefined); // Clear selection when changing views
  };

  const handleNoteSelect = (noteId: string) => {
    setSelectedNoteId(noteId);
    setIsEditing(true);
  };

  const getCurrentViewTitle = () => {
    switch (currentView) {
      case 'folder':
        const folder = folders.find(f => f.id === selectedFolderId);
        return folder?.name || 'Folder';
      case 'favorites':
        return 'Favorites';
      case 'recent':
        return 'Recent';
      case 'all':
        return 'All Notes';
      default:
        return 'Notes';
    }
  };

  const currentNotes = getCurrentNotes();
  const selectedNote = selectedNoteId ? notes.find(n => n.id === selectedNoteId) : undefined;

  return (
    <div className="h-full flex flex-col">
      <WindowHeader 
        title="Notes"
        subtitle={`${currentNotes.length} notes`}
      />

      <WindowContent padding={false} className="flex-1 h-full overflow-auto">
        <MacOSNotesLayout
          isEditing={isEditing}
          onToggleEditing={handleToggleEditing}
          sidebar={
            <NotesNavigation
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              currentView={currentView}
              onViewChange={handleViewChange}
              selectedFolderId={selectedFolderId}
              onNewNote={handleNewNote}
            />
          }
          notesList={
            <NotesListPanel
              notes={currentNotes}
              selectedNoteId={selectedNoteId}
              onSelectNote={handleNoteSelect}
              onNewNote={handleNewNote}
              viewTitle={getCurrentViewTitle()}
              searchQuery={searchQuery}
            />
          }
          noteContent={
            selectedNote ? (
              <NoteContentPanel 
                note={selectedNote}
                onEditingChange={setIsEditing}
              />
            ) : undefined
          }
        />
      </WindowContent>
    </div>
  );
}